import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Phone, MessageCircle, Home } from "lucide-react";
import { useEffect, useRef } from "react";
import { SITE } from "@/config/site";

declare global {
  interface Window {
    gtag?: (...args: (string | number | object | undefined)[]) => void;
  }
}

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: "Thank you — IZHUB Cleaning Service" },
      { name: "description", content: "Your quote request has been received. The IZHUB team will be in touch shortly." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Thank you — IZHUB Cleaning Service" },
      { property: "og:description", content: "Your quote request has been received. The IZHUB team will be in touch shortly." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: ThankYouPage,
});

function ThankYouPage() {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (typeof window === "undefined") return;

    let successfulSubmission = false;
    try {
      successfulSubmission = sessionStorage.getItem("quote_submission_success") === "true";
    } catch {
      successfulSubmission = false;
    }
    console.log("[thank-you] session flag =", successfulSubmission);
    if (!successfulSubmission) return;

    firedRef.current = true;

    // Ensure dataLayer + gtag shim exist immediately, even if gtag.js hasn't
    // finished loading yet. Queued calls will be flushed when the tag loads.
    type GtagArgs = (string | number | object | undefined)[];
    const w = window as unknown as {
      dataLayer?: unknown[];
      gtag?: (...args: GtagArgs) => void;
    };
    if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
    if (typeof w.gtag !== "function") {
      w.gtag = function gtag(...args: GtagArgs) {
        w.dataLayer!.push(args);
      };
      console.log("[thank-you] gtag shim installed (script not loaded yet)");
    } else {
      console.log("[thank-you] gtag available");
    }

    const fireEvents = () => {
      const gtag = w.gtag!;
      try {
        gtag("event", "generate_lead", {
          form_name: "quote_request",
          lead_type: "cleaning_quote",
          page_location: window.location.href,
          page_title: document.title,
        });
        console.log("[thank-you] fired generate_lead");

        gtag("event", "ads_conversion_Request_quote_1");
        console.log("[thank-you] fired ads_conversion_Request_quote_1");

        gtag("event", "conversion", {
          send_to: "AW-18310525767/LD-4CJ-YptMcEMfmkZtE",
        });
        console.log("[thank-you] fired Google Ads conversion");

        // NOTE: no send_to here — previously it was scoped to Ads only,
        // which is why quote_submitted never appeared in GA4.
        gtag("event", "quote_submitted", {
          event_category: "quote",
          event_label: "thank_you_page",
          value: 1,
        });
        console.log("[thank-you] fired quote_submitted");

        try {
          sessionStorage.removeItem("quote_submission_success");
        } catch {
          /* ignore */
        }
      } catch (err) {
        console.error("[thank-you] gtag fire error", err);
      }
    };

    // Fire immediately (queues into dataLayer if the tag is still loading),
    // then also on window load as a safety net.
    fireEvents();
    if (document.readyState !== "complete") {
      window.addEventListener("load", () => {
        console.log("[thank-you] window load — gtag now:", typeof w.gtag);
      }, { once: true });
    }
  }, []);


  return (
    <div className="container-x py-20 md:py-28">
      <div className="mx-auto max-w-2xl rounded-3xl border border-border/60 bg-background/85 p-8 text-center shadow-sm backdrop-blur-sm md:p-12">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-soft">
          <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>
        <h1 className="mt-6 font-display text-3xl md:text-4xl">Thank you for your quote request</h1>
        <p className="mt-4 text-muted-foreground">
          We have received your details and will respond as soon as possible — usually within 2 hours during business hours.
        </p>

        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <a
            href={`tel:${SITE.phone}`}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" />
            {SITE.phoneDisplay}
          </a>
          <a
            href={SITE.whatsapp}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-sage-soft px-5 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-sage-soft/80"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp us
          </a>
        </div>

        <div className="mt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium transition-colors hover:bg-muted"
          >
            <Home className="h-4 w-4" />
            Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
}
